import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, JSX, useEffect } from 'react';
import { Formik } from 'formik';
import { BankAccountTransferHandler, BankAccountTransferRequest } from './types';
import { buildBankAccountTransferRows } from './buildBankAccountTransferRows.helper';
import { usePendingData } from '../../../providers/PendingData.provider';
import { useClientData } from '@/providers/ClientData.provider';

const { Box, Table, TextField, Select, Icon } = vp;

type BankAccountTransferProps = {
  handleToken: (token: string) => void;
  handlers: BankAccountTransferHandler;
  defaultValues: BankAccountTransferRequest;
  sourceBank?: vp.ClientBankAccountsLists;
  onDestBankChange: (BankInfo?: vp.ClientBankAccountsLists) => void;
  destBank?: vp.ClientBankAccountsLists;
  onSourceBankChange: (BankInfo?: vp.ClientBankAccountsLists) => void;
};

type FC = FunctionComponent<BankAccountTransferProps>;
const BankAccountTransferForm: FC = ({
  defaultValues,
  handleToken,
  handlers,
  sourceBank,
  onSourceBankChange,
  destBank,
  onDestBankChange,
}) => {
  const {
    response: { Country },
  } = useClientData();
  const bankAccountTransferRow = buildBankAccountTransferRows({
    sourceBank,
    onSourceBankChange,
    destBank,
    onDestBankChange,
    country: Country,
  });

  const { initPendingData, updatePendingData } = usePendingData();

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={handlers.onReview}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit, submitForm }) => {
        useEffect(() => {
          handlers.onChange(values, errors, touched, submitForm, bankAccountTransferRow);
        }, [values, errors]);
        useEffect(() => {
          initPendingData({ ...values, Amount: values.Amount?.toString() || '0' });
        }, []);
        useEffect(() => {
          updatePendingData({ ...values, Amount: values.Amount?.toString() || '0' });
        }, [values]);

        const accountTransferTable: vp.TableProps = {
          name: 'bank-account-transfer-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: bankAccountTransferRow
            .filter(({ visible }) => visible)
            .map(
              ({
                label,
                id,
                require,
                preset,
                type,
                placeholder,
                getSelectOptions,
                allowNewOption,
                disableFiltering,
                searchCallback,
                SelectLabel,
                onChangeCallBack,
              }) => ({
                Field: (
                  <Box ai="center">
                    {label} {getRequiredIcon(require)}
                  </Box>
                ),
                Input:
                  type === 'input' ? (
                    <TextField
                      preset={preset}
                      key={id}
                      name={id}
                      variant="standard"
                      value={String(values[id])}
                      placeholder={placeholder}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={errors[id] && touched[id] ? errors[id] : null}
                    />
                  ) : (
                    <Select
                      variant="standard"
                      options={getSelectOptions ? getSelectOptions() : []}
                      labelOverride={SelectLabel ? SelectLabel() : undefined}
                      key={id}
                      name={id}
                      value={String(values[id])}
                      placeholder={placeholder}
                      noOrdering
                      allowNew={allowNewOption}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        id === 'From' ? handleToken(values?.From || '') : handleChange(e);
                        if (onChangeCallBack) {
                          onChangeCallBack(e);
                        }
                      }}
                      error={errors[id] && touched[id] ? errors[id] : null}
                      disableFiltering={disableFiltering ? disableFiltering() : undefined}
                      searchCallback={disableFiltering ? (disableFiltering() ? searchCallback : undefined) : undefined}
                    />
                  ),
              }),
            ),
        };

        return (
          <form onSubmit={handleSubmit}>
            <Table {...accountTransferTable} />
          </form>
        );
      }}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      Currency: Yup.string().required('This value is required.'),
      From: Yup.string().required('This value is required.'),
      To: Yup.string().required('This value is required.'),
      Amount: Yup.string()
        .test('amount-validation', 'This value should be greater than or equal to $0.01.', (value) => {
          const amountValidation = Number(value) <= 0.0;
          return !amountValidation;
        })
        .required('This value is required.'),
    });
  }
  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
};

export default BankAccountTransferForm;
